import React, { useEffect, useRef, useState } from 'react';
import { Analysis } from '../../types';
import './styles.scss';

const TYPE_ICON: Record<string, string> = {
  vegetation_index: '🌱',
  water_quality: '💧',
  air_quality: '🌫️',
  weather_forecast: '🌤️',
  video_animation: '🎞️',
  business_location: '📍',
};

/** Public author label: explicit display name → e-mail prefix → generic. */
export const authorLabel = (item: Pick<Analysis, 'user_name' | 'user_email'>): string => {
  const name = (item.user_name || '').trim();
  if (name) return name;
  const prefix = (item.user_email || '').split('@')[0].trim();
  if (prefix) return prefix.replace(/[._-]/g, ' ');
  return 'LLEO user';
};

/**
 * Short, human-readable unique ID for a card.  Session IDs look like
 * `20261029_135047_nPeceUx-gSo`; the trailing random segment is unique on
 * its own, so we show that (upper-cased) as a compact reference.
 */
export const shortId = (item: Pick<Analysis, 'id' | 'session_id'>): string => {
  const raw = item.session_id || item.id || '';
  const tail = raw.split('_').pop() || raw;
  return tail.slice(0, 11).toUpperCase();
};

interface Props {
  item: Analysis;
  src: string;
  alt?: string;
  /** Icon to show when the image is unavailable (defaults per analysis type). */
  fallbackIcon?: string;
}

// Retry schedule (ms) for transient load failures (cold Cloud Run instance,
// GCS hiccup, throttled parallel requests).  A permanent 404 still ends in the
// placeholder once the schedule is exhausted.
const RETRY_DELAYS = [800, 2000, 4500];

/**
 * Card thumbnail that degrades gracefully: when the image repeatedly fails to
 * load or decodes to a 0×0 image, a styled placeholder is rendered instead of
 * a broken/blank <img>.
 *
 * Previews never expire: the backend rebuilds a missing `preview.png` on
 * demand, so even rows flagged `artifacts_expired` are *attempted* first and
 * only fall back to the placeholder if the request really fails.
 *
 * Transient failures are retried automatically with a cache-busting query
 * param so the user no longer has to refresh the page to see thumbnails.
 */
const AnalysisThumbnail: React.FC<Props> = ({ item, src, alt, fallbackIcon }) => {
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const timerRef = useRef<number | null>(null);

  const expired = !!item.artifacts_expired;
  const icon = fallbackIcon || TYPE_ICON[item.analysis_type] || '🗺️';

  // Reset when the card is re-used for a different analysis / URL.
  useEffect(() => {
    setAttempt(0);
    setFailed(false);
    setLoaded(false);
  }, [src, item.session_id]);

  useEffect(() => () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
  }, []);

  const scheduleRetry = () => {
    if (attempt >= RETRY_DELAYS.length) {
      setFailed(true);
      return;
    }
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setAttempt((a) => a + 1), RETRY_DELAYS[attempt]);
  };

  if (failed || !src) {
    return (
      <div className="analysis-thumbnail-placeholder" title="Preview unavailable">
        <span className="placeholder-icon">{icon}</span>
        {expired && <span className="placeholder-caption">Preview unavailable</span>}
      </div>
    );
  }

  // Only bust the cache on retries; the first request should hit the browser
  // / CDN cache so repeat visits are instant.
  const effectiveSrc = attempt === 0 ? src : `${src}${src.includes('?') ? '&' : '?'}r=${attempt}`;

  return (
    <div className={`analysis-thumbnail-frame ${loaded ? 'is-loaded' : 'is-loading'}`}>
      {!loaded && (
        <div className="analysis-thumbnail-skeleton" aria-hidden="true">
          <span className="placeholder-icon">{icon}</span>
        </div>
      )}
      <img
        key={effectiveSrc}
        loading="lazy"
        decoding="async"
        src={effectiveSrc}
        alt={alt || item.analysis_type}
        className="analysis-thumbnail"
        onError={scheduleRetry}
        onLoad={(e) => {
          const img = e.currentTarget;
          if (!img.naturalWidth || !img.naturalHeight) {
            scheduleRetry();
            return;
          }
          setLoaded(true);
        }}
      />
    </div>
  );
};

export default AnalysisThumbnail;
